import { getPlatformInviteFlavorString, matchesInviteFlavorString } from 'Routes/MyService/util';
import { fetchWithOptions } from 'Utility/fetch';
import { getJWTData, parseJWTFromCookie } from 'Utility/jwtAuthTools';
import { localStorageGet } from 'Utility/localStorage';
import { ActionableErrorContext, Loggable, makeLog } from 'Utility/logger';
import { sortAndMergeDocuments } from 'Utility/documentHandling';
import type { TTaskV3 } from '@stellarq/tasks';
import { convertTaskListToTaskV3RequestBody, convertTTaskV3ToTasklist } from 'Utility/tasklist';
import { isPlatform } from 'neuro-constants';

export type TPatientDevice = {
  deviceId: string;
  userId: string;
  deviceModel: string;
  jwtId: string;
  expirationTime: number;
};

/**
 * Get array of devices for patient
 * @param {IPatientAPI['id']} patientId - Patient id
 * @returns {Promise<Array<TPatientDevice>>} Promise array of the devices for patient or empty array
 */
export const getPatientDevices = async (patientId: IPatientAPI['id']): Promise<Array<TPatientDevice>> => {
  try {
    const response = await fetchWithOptions(
      `/api/devices/${patientId}`,
      { neurojwt: parseJWTFromCookie() },
      { method: 'GET' },
    );
    if (response.ok && response.status === 200) return await response.json();
    else throw new Error(`unexpected status: ${response.status}`);
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'GET patient devices'));
    return [];
  }
};

/**
 * Post tasklist
 * @param {ITaskList} tasklist data to post
 */
export const sendTaskList = async (tasklist: ITaskList): Promise<TTaskV3 | null> => {
  const { patientid: patientId, orgid: orgId } = getJWTData() ?? {};
  const platform = localStorageGet('platform');

  if (!patientId || !orgId || !isPlatform(platform)) return null;

  try {
    const body = convertTaskListToTaskV3RequestBody({ tasklist, platform });
    const response = await fetchWithOptions(
      '/api/patient/v3/task',
      { neurojwt: parseJWTFromCookie() },
      { method: 'POST', body: JSON.stringify(body) },
    );
    if (response.ok && response.status === 200) {
      const temp = await response.json();
      return temp;
    } else throw new Error(`unexpected status: ${response.status}`);
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'POST task'));
    return null;
  }
};

/**
 * Update tasklist
 * @param {ITaskList} tasklist data to post
 */
export const updateTaskListFetcher = async (tasklist: ITaskList): Promise<TTaskV3 | null> => {
  // const { patientid: patientId, orgid: orgId } = getJWTData() ?? {};
  const platform = localStorageGet('platform');

  if (!isPlatform(platform)) return null;

  try {
    const body = convertTaskListToTaskV3RequestBody({ tasklist, platform });
    const response = await fetchWithOptions(
      `/api/patient/v3/task/${tasklist.id}`,
      { neurojwt: parseJWTFromCookie() },
      { method: 'PATCH', body: JSON.stringify(body) },
    );
    if (response.ok && response.status === 200) return await response.json();
    else throw new Error(`unexpected status: ${response.status}`);
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'PATCH task'));
    return null;
  }
};

/**
 * Delete tasklist
 * @param {ITaskList} tasklist - Tasklist to be deleted
 */
export const deleteTaskListFetcher = async (tasklist: ITaskList): Promise<boolean> => {
  try {
    const response = await fetchWithOptions(
      `/api/patient/v3/task/${tasklist.id}`,
      { neurojwt: parseJWTFromCookie() },
      { method: 'DELETE' },
    );
    if (response.ok) return true;
    else throw new Error(`unexpected status: ${response.status}`);
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'DELETE task'));
    return false;
  }
};

export const getTaskLists = async (): Promise<Array<ITaskList> | null> => {
  try {
    const response = await fetchWithOptions(
      '/api/patient/v3/task?expired=true&IROM=true',
      { neurojwt: parseJWTFromCookie() },
      { method: 'GET' },
    );
    if (response.status !== 200) throw new Error('unexpected status ' + response.status);
    if (!response.ok) throw new Error('response not ok ' + response.status);
    if (!('json' in response)) throw new Error('no response.json');

    const data = (await response.json()) as unknown;
    if (!Array.isArray(data)) throw new Error('expected data to be an array');

    const tasks: TTaskV3[] = data as TTaskV3[];
    // for (const task of data) {
    //   // implement some kind of validation?
    //   tasks.push(task as TTaskV3);
    // }
    return tasks.map(task => convertTTaskV3ToTasklist({ task }));
  } catch (error: unknown) {
    if (error instanceof Error) makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'GET tasks'));
    return null;
  }
};

/**
 * Send Invite to use MyService
 * @param {string} carrier email address or phone number
 * @param {string} medium  'EMAIL' | 'SMS'
 * @param {string} orgId organization ID
 * @param {Locale} lang - 2-letter ISO 639 code of the invite recipient language
 */
export const sendMyInvite = (carrier: string, medium: Uppercase<ContactInfoType>, orgId: string, locale: Locale) =>
  fetchWithOptions(
    '/api/patient/v2/invite/',
    { neurojwt: parseJWTFromCookie() },
    {
      method: 'POST',
      body: JSON.stringify({
        carrier,
        medium,
        flavor: getPlatformInviteFlavorString(),
        org_id: orgId,
        locale,
      }),
    },
  );

/**
 * Create new document with finished commit
 * @param {string} documentType documentType
 * @param {IControlProps} data data of the document created
 * @returns object with documentId and commitId if creation was succesful
 */
export const createNewDocumentWithCommit = async (
  documentType: TDocumentType,
  data: TAnyObject,
): Promise<{ documentId: string; commitId: string } | null> => {
  try {
    const response = await fetchWithOptions(
      `/api/mydocuments/${documentType}`,
      { neurojwt: parseJWTFromCookie() },
      // do we want to implement some validation here?
      { method: 'PUT', body: JSON.stringify(data) },
    );
    if (response.ok && response.status === 200) return await response.json();
    else throw new Error(`unexpected status: ${response.status}`);
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'PUT document MySQ'));
    return null;
  }
};

/**
 * Create a finished commit to a document
 * @param {string} documentType documentType
 * @param {string} documentId docId
 * @param {TAnyObject} data data of the commit
 * @returns Object with the new commitId if the creation was succesful
 */
export const createCommitToDocument = async (
  documentType: TDocumentType,
  documentId: string,
  data: TAnyObject,
): Promise<{ commitId: string } | null> => {
  try {
    const response = await fetchWithOptions(
      `/api/mydocuments/${documentType}/${documentId}`,
      { neurojwt: parseJWTFromCookie() },
      // do we want to implement some validation here?
      { method: 'PUT', body: JSON.stringify(data) },
    );
    if (response.ok && response.status === 200) return await response.json();
    else throw new Error(`unexpected status: ${response.status}`);
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'PUT commit MySQ'));
    return null;
  }
};

/**
 * Delete document
 * @param {string} documentType documentType
 * @param {string} documentId docId
 * @returns Document deletion success
 */
export const deleteDocumentFetcher = async (
  documentType: TDocumentType,
  documentId: string,
  reason: Record<string, string>,
): Promise<boolean> => {
  try {
    const response = await fetchWithOptions(
      `/api/mydocuments/${documentType}/${documentId}`,
      { neurojwt: parseJWTFromCookie() },
      { method: 'DELETE', body: JSON.stringify({ reason, date: Date.now() }) },
    );
    if (response.ok && response.status === 200) return true;
    else throw new Error(`unexpected status: ${response.status}`);
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'DELETE document MySQ'));
    return false;
  }
};

/**
 * Fetch invites (to become a MySQ user) sent to the patient (associated with
 * the current session).
 */
export const getMyAppInvites = async <Invite extends { flavor: string }>(): Promise<Invite[]> => {
  try {
    const response = await fetchWithOptions(
      '/api/patient/v2/stimulus', // API ~ neuro-api:0.19.4, neuro-patient-service:0.28.1
      { neurojwt: parseJWTFromCookie() },
      { method: 'GET' },
    );
    if (response.ok && response.status === 200) {
      const invites = (await response.json()) as INeuroStimulus[];
      const filtered = invites.filter(invite => matchesInviteFlavorString(invite.flavor)) as unknown as Invite[];
      return filtered;
    } else throw new Error(`unexpected status: ${response.status}`);
  } catch (err) {
    makeLog(
      Loggable.BugActionable,
      err instanceof Error ? err : new Error('strange error'),
      new ActionableErrorContext(null, 'GET sent invites log'),
    );
    return [];
  }
};

/**
 * Fetch MySQ user identity of the patient (associated with the current session).
 */
export const getMyAppUserIdentity = async (): Promise<string | null> => {
  try {
    const response = await fetchWithOptions(
      '/api/vault-mysq/v2/user/ssn',
      { neurojwt: parseJWTFromCookie() },
      { method: 'GET' },
    );
    if (response.ok && response.status === 200) {
      const body = (await response.json()) as { userId: string } | null;
      if (typeof body?.userId === 'string') return body.userId;
      else return null;
    }
    throw new Error(`unexpected status: ${response.status}`);
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'GET user identity MySQ'));
    return null;
  }
};

/**
 * Get "My Service" data docs of the patient (associated with the current
 * session).
 */
export const getMyAppDocs = async (): Promise<IDocumentV2[]> => {
  try {
    const response = await fetchWithOptions(
      '/api/mydocuments', // API ~ neuro-api:0.19.4
      { neurojwt: parseJWTFromCookie() },
      { method: 'GET' },
    );
    if (response.ok && response.status === 200) {
      const docs = (await response.json()) as INeuroDocument[];
      return sortAndMergeDocuments(docs);
    }
    throw new Error(`unexpected status: ${response.status}`);
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'GET documents MySQ'));
    return [];
  }
};

type TMyUserContactResponse = {
  userId: string;
  contactType: string;
  contactValue: Array<string>;
  isContactVerified: boolean;
};

export const fetchMyUserContactInfo = async (userId: string): Promise<Array<TMyUserContactResponse> | null> => {
  try {
    const response = await fetchWithOptions(
      `/api/vault-mysq/v2/informant/contacts/${userId}`,
      { neurojwt: parseJWTFromCookie() },
      { method: 'GET' },
    );
    if (response.ok && response.status === 200) return await response.json();
    else throw new Error(`unexpected status: ${response.status}`);
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'GET mysq user contact info'));
    return null;
  }
};

type TRespUserData = {
  userId: string;
  organization: string;
  firstNames: string;
  lastNames: string;
  active: boolean;
  uILanguage: string;
};

export const fetchMyUserData = async (userId: string): Promise<TRespUserData | null> => {
  try {
    const response = await fetchWithOptions(
      `/api/vault-mysq/v2/informant/user/${userId}`,
      { neurojwt: parseJWTFromCookie() },
      { method: 'GET' },
    );
    if (response.ok && response.status === 200) return await response.json();
    else throw new Error(`unexpected status: ${response.status}`);
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'GET mysq user data'));
    return null;
  }
};

export const fetchMyUserDataBySsn = async ({
  ssn,
  ssnLocalization,
}: {
  ssn: string;
  ssnLocalization: PatientCountryCode;
}): Promise<TRespUserData | null> => {
  try {
    const response = await fetchWithOptions(
      `/api/vault-mysq/v2/user/ssn/${ssnLocalization}/${ssn}`,
      { neurojwt: parseJWTFromCookie() },
      { method: 'GET' },
    );
    if (response.ok && response.status === 200) return await response.json();
    else if (response.status === 204) return null;
    else throw new Error(`unexpected status: ${response.status}`);
  } catch (error: unknown) {
    makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'GET mysq user data by ssn'));
    return null;
  }
};
